import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { TabPanel } from 'react-tabs';
import BackButton from '../../../components/general/BackButton';
import HeaderTitleView from '../../../components/structure/HeaderTitleView';
import { BreadcrumbItem } from '../../../components/general/Breadcrumb';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import TemplateConfigurationModel from '../../../models/templating/api/TemplateConfigurationModel';
import TemplateConfigurationContainer from '../../../state/containers/TemplateConfigurationContainer';
import PageError from '../../../components/general/PageError';
import FullPageTabbedPanel from '../../../components/structure/Panels/FullPageTabbedPanel';
import { FormikProps, withFormik } from 'formik';
import TemplateSetupEditor from './TemplateSetupEditor';
import _ from 'lodash';
import TemplateConfigurationMenuSelector from './TemplateConfigurationMenuSelector';
import MediaContainer from '../../../state/containers/MediaContainer';
import TemplateDetailsEditor from './TemplateDetailsEditor';
import AppliedApplicationTemplateContainer from '../../../state/containers/AppliedApplicationTemplateContainer';
import TemplateSaveChangesModal, { ChangedFieldModel } from '../../../components/templating/TemplateSaveChangesModal';
import TemplateFormFieldModel from '../../../models/templating/api/TemplateFormFieldModel';
import { ConditionalCondition, CONDITIONAL_OPERATOR_LABELS } from '../../../models/templating/api/ConditionalDefaultModel';
import { FIELD_TYPE_OPTIONS } from './FieldTypeChangeSelector';

const builderIcon = require('../../../content/images/tab-icons/template-builder.svg');
const setupIcon = require('../../../content/images/tab-icons/setup.svg');

interface TemplateConfigurationContentProps {
    templateConfigurationId?: string
    mediaContainer: MediaContainer
    orgContainer: OrganizationContainer
    templateConfigContainer: TemplateConfigurationContainer
    templateConfiguration: TemplateConfigurationModel
    history: any
    appliedApplicationTemplateContainer: AppliedApplicationTemplateContainer
}

interface TemplateConfigurationFormData {
    templateConfiguration: TemplateConfigurationModel
}

const DIFFED_FIELD_LABELS: { key: keyof TemplateFormFieldModel; label: string }[] = [
    { key: 'fieldType', label: 'Field Type' },
    { key: 'title', label: 'Display Title' },
    { key: 'label', label: 'Text Field Label' },
    { key: 'tip', label: 'Description of Field' },
    { key: 'placeholder', label: 'Placeholder Content' },
    { key: 'defaultValue', label: 'Default Value' },
    { key: 'associatedVariable', label: 'Associated Variable' },
    { key: 'isRequired', label: 'Required Field' },
];

const formatCondition = (c: ConditionalCondition): string => {
    const op = CONDITIONAL_OPERATOR_LABELS[c.operator] ?? c.operator;
    const base = `If ${c.fieldAssociatedVariable} ${op}${c.value !== undefined ? ` "${c.value}"` : ''} → "${c.defaultValue}"`;
    return base;
};

const diffTemplateFields = (
    saved: TemplateConfigurationModel,
    current: TemplateConfigurationModel
): ChangedFieldModel[] => {
    const changes: ChangedFieldModel[] = [];
    const savedSections = saved?.templateForm?.templateFormSections ?? [];
    const currentSections = current?.templateForm?.templateFormSections ?? [];

    // Section-level diff
    const savedSectionsById = new Map(savedSections.map(s => [s.id, s]));
    const currentSectionsById = new Map(currentSections.map(s => [s.id, s]));

    currentSectionsById.forEach((section, id) => {
        if (!savedSectionsById.has(id)) {
            changes.push({
                fieldName: section.title || 'New section',
                oldValue: '',
                newValue: '',
                state: 'Added',
            });
        }
    });

    savedSectionsById.forEach((section, id) => {
        if (!currentSectionsById.has(id)) {
            changes.push({
                fieldName: section.title || 'Removed section',
                oldValue: '',
                newValue: '',
                state: 'Removed',
            });
        }
    });

    // Field-level diff — build maps keyed by field id, recording which section each belongs to
    const savedFieldsById = new Map<string, { field: TemplateFormFieldModel; sectionTitle: string }>();
    savedSections.forEach(s => (s.templateFormFields ?? []).forEach(f =>
        savedFieldsById.set(f.id, { field: f, sectionTitle: s.title || '' })
    ));

    const currentFieldsById = new Map<string, { field: TemplateFormFieldModel; sectionTitle: string }>();
    currentSections.forEach(s => (s.templateFormFields ?? []).forEach(f =>
        currentFieldsById.set(f.id, { field: f, sectionTitle: s.title || '' })
    ));

    // Modified or Added fields
    currentFieldsById.forEach(({ field: currentField, sectionTitle: currentSection }, id) => {
        const saved = savedFieldsById.get(id);
        const fieldDisplayName = currentField.title || currentField.label || 'New field';

        if (!saved) {
            changes.push({
                fieldName: currentSection ? `${currentSection} — ${fieldDisplayName}` : fieldDisplayName,
                oldValue: '',
                newValue: '',
                state: 'Added',
            });
        } else {
            const { field: savedField, sectionTitle: savedSection } = saved;

            // Field moved to a different section
            if (savedSection !== currentSection) {
                changes.push({
                    fieldName: fieldDisplayName,
                    oldValue: savedSection || '(none)',
                    newValue: currentSection || '(none)',
                    state: 'Modified',
                });
            }

            // Property-level changes
            DIFFED_FIELD_LABELS.forEach(({ key, label }) => {
                const rawOld = savedField[key];
                const rawNew = currentField[key];
                // Normalise to string for comparison; treat undefined/null as empty.
                const oldVal = rawOld != null ? String(rawOld) : '';
                const newVal = rawNew != null ? String(rawNew) : '';
                if (oldVal !== newVal) {
                    const formatVal = (v: string) => {
                        if (key === 'fieldType') {
                            return FIELD_TYPE_OPTIONS.find(o => o.value === v)?.label ?? v;
                        }
                        if (key === 'isRequired') {
                            return v === 'true' ? 'Yes' : 'No';
                        }
                        return v;
                    };
                    changes.push({
                        fieldName: `${fieldDisplayName} — ${label}`,
                        oldValue: formatVal(oldVal),
                        newValue: formatVal(newVal),
                        state: 'Modified',
                    });
                }
            });

            // Conditional defaults diff
            const savedConditions = savedField.conditionalDefaults?.conditions ?? [];
            const currentConditions = currentField.conditionalDefaults?.conditions ?? [];
            const savedOtherwise = savedField.conditionalDefaults?.otherwise ?? '';
            const currentOtherwise = currentField.conditionalDefaults?.otherwise ?? '';

            const maxLen = Math.max(savedConditions.length, currentConditions.length);
            for (let i = 0; i < maxLen; i++) {
                const savedCond = savedConditions[i];
                const currentCond = currentConditions[i];
                if (!savedCond) {
                    changes.push({
                        fieldName: `${fieldDisplayName} — Conditional Default`,
                        oldValue: '',
                        newValue: formatCondition(currentCond),
                        state: 'Added',
                    });
                } else if (!currentCond) {
                    changes.push({
                        fieldName: `${fieldDisplayName} — Conditional Default`,
                        oldValue: formatCondition(savedCond),
                        newValue: '',
                        state: 'Removed',
                    });
                } else {
                    const oldStr = formatCondition(savedCond);
                    const newStr = formatCondition(currentCond);
                    if (oldStr !== newStr) {
                        changes.push({
                            fieldName: `${fieldDisplayName} — Conditional Default`,
                            oldValue: oldStr,
                            newValue: newStr,
                            state: 'Modified',
                        });
                    }
                }
            }

            if (savedOtherwise !== currentOtherwise) {
                changes.push({
                    fieldName: `${fieldDisplayName} — Conditional Default (Otherwise)`,
                    oldValue: savedOtherwise,
                    newValue: currentOtherwise,
                    state: 'Modified',
                });
            }

            // Options diff (for Select / Radio / Checkbox fields)
            const savedOptions = savedField.options ?? [];
            const currentOptions = currentField.options ?? [];
            const savedOptionsById = new Map(savedOptions.map(o => [o.id, o]));
            const currentOptionsById = new Map(currentOptions.map(o => [o.id, o]));

            currentOptionsById.forEach((opt, oid) => {
                const savedOpt = savedOptionsById.get(oid);
                if (!savedOpt) {
                    changes.push({
                        fieldName: `${fieldDisplayName} — Option`,
                        oldValue: '',
                        newValue: opt.label || opt.value || '(empty)',
                        state: 'Added',
                    });
                } else {
                    if (savedOpt.label !== opt.label || savedOpt.value !== opt.value) {
                        changes.push({
                            fieldName: `${fieldDisplayName} — Option`,
                            oldValue: `${savedOpt.label || ''}${savedOpt.value ? ` (${savedOpt.value})` : ''}`,
                            newValue: `${opt.label || ''}${opt.value ? ` (${opt.value})` : ''}`,
                            state: 'Modified',
                        });
                    }
                }
            });

            savedOptionsById.forEach((opt, oid) => {
                if (!currentOptionsById.has(oid)) {
                    changes.push({
                        fieldName: `${fieldDisplayName} — Option`,
                        oldValue: opt.label || opt.value || '(empty)',
                        newValue: '',
                        state: 'Removed',
                    });
                }
            });

            // Detect reordering: if the id sequences differ
            const savedOrderIds = savedOptions.map(o => o.id).join(',');
            const currentOrderIds = currentOptions.map(o => o.id).join(',');
            if (savedOrderIds !== currentOrderIds && savedOrderIds !== '' && currentOrderIds !== '') {
                // Only flag reorder if the set of ids is the same (pure reorder, not add/remove)
                const savedSet = new Set(savedOptions.map(o => o.id));
                const currentSet = new Set(currentOptions.map(o => o.id));
                const sameSet = savedOptions.every(o => currentSet.has(o.id)) && currentOptions.every(o => savedSet.has(o.id));
                if (sameSet) {
                    changes.push({
                        fieldName: `${fieldDisplayName} — Options order`,
                        oldValue: savedOptions.map(o => o.label || o.value).join(', '),
                        newValue: currentOptions.map(o => o.label || o.value).join(', '),
                        state: 'Modified',
                    });
                }
            }
        }
    });

    // Removed fields
    savedFieldsById.forEach(({ field: savedField, sectionTitle: savedSection }, id) => {
        if (!currentFieldsById.has(id)) {
            const fieldDisplayName = savedField.title || savedField.label || 'Removed field';
            changes.push({
                fieldName: savedSection ? `${savedSection} — ${fieldDisplayName}` : fieldDisplayName,
                oldValue: '',
                newValue: '',
                state: 'Removed',
            });
        }
    });

    // Field order diff — detect drag-and-drop reorders within each section
    savedSectionsById.forEach((savedSection, id) => {
        const currentSection = currentSectionsById.get(id);
        if (!currentSection) return;

        const savedOrder = _.sortBy(savedSection.templateFormFields ?? [], f => f.priority).map(f => f.id);
        const currentOrder = _.sortBy(currentSection.templateFormFields ?? [], f => f.priority).map(f => f.id);

        if (savedOrder.join(',') === currentOrder.join(',')) return;

        // Only flag as a reorder if the set of ids is unchanged (no additions/removals)
        const savedSet = new Set(savedOrder);
        const currentSet = new Set(currentOrder);
        const sameSet = savedOrder.every(id => currentSet.has(id)) && currentOrder.every(id => savedSet.has(id));
        if (!sameSet) return;

        const sectionTitle = currentSection.title || savedSection.title || 'Section';
        const fieldLabel = (id: string) => {
            const f = (currentSection.templateFormFields ?? []).find(f => f.id === id);
            return f?.title || f?.label || id;
        };
        changes.push({
            fieldName: `${sectionTitle} — Field order`,
            oldValue: savedOrder.map(fieldLabel).join(', '),
            newValue: currentOrder.map(fieldLabel).join(', '),
            state: 'Modified',
        });
    });

    return changes;
};

const TemplateConfigurationContent: React.FC<TemplateConfigurationContentProps & FormikProps<TemplateConfigurationFormData>> = (props) => {

    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveChangedFields, setSaveChangedFields] = useState<ChangedFieldModel[]>([]);
    const [savedSnapshot, setSavedSnapshot] = useState<TemplateConfigurationModel>(() => JSON.parse(JSON.stringify(props.templateConfiguration ?? null)));

    useEffect(() => {
        // When the server prop changes (initial load or after a successful save), update the snapshot
        setSavedSnapshot(JSON.parse(JSON.stringify(props.templateConfiguration ?? null)));
    }, [props.templateConfiguration?.id, props.templateConfiguration?.modifiedDate]);

    useEffect(() => {
        const loadTemplateConfigurations = async () => {
            await props.templateConfigContainer.getOrganizationTemplates(props.orgContainer.state.currentOrganization.id)
        }
        if (props.templateConfiguration == null) {
            loadTemplateConfigurations();
        }
        props.templateConfigContainer.loadCategories();
        props.templateConfigContainer.loadIndustries();
        props.setFieldValue("templateConfiguration", props.templateConfiguration);
    }, [props.templateConfiguration]);


    const handleClose = () => {
        props.history.push(`/v/templates`);
    }

    const getBreadCrumbItems = () => {
        var crumbs: BreadcrumbItem[] = [{
            id: 'org',
            title: props.orgContainer.state.currentOrganization?.name,
            link: '/'
        }]

        return crumbs;
    }

    const handleTemplateUpdate = (template: TemplateConfigurationModel) => {
        props.setFieldValue("templateConfiguration", template);
    }


    const { isSaving } = props.templateConfigContainer.state;
    return (
        <form onSubmit={props.handleSubmit}>
            <FullPageTabbedPanel tabs={[{
                title: "Setup",
                icon: setupIcon,
                selectedIcon: setupIcon
            }, {
                title: "Form Creator",
                icon: builderIcon,
                selectedIcon: builderIcon
            }
            ]}
                leftRender={() => <>
                    <BackButton onClick={handleClose.bind(this)} />
                    <TemplateConfigurationMenuSelector
                        templateConfiguration={props.values.templateConfiguration}
                        templateConfigContainer={props.templateConfigContainer}
                        currentTemplateId={props.values.templateConfiguration?.id}
                        history={props.history} />
                </>}
                titleRender={() => <HeaderTitleView text={props.templateConfiguration?.name} breadcrumb={getBreadCrumbItems()} />}
                rightRender={() => null}>
                <TabPanel>
                    <TemplateSetupEditor
                        history={props.history}
                        onChange={handleTemplateUpdate.bind(this)}
                        isSaving={isSaving} templateConfigurationContainer={props.templateConfigContainer}
                        templateConfiguration={props.values.templateConfiguration}
                        mediaContainer={props.mediaContainer}
                        organizationId={props.orgContainer.state.currentOrganization?.id} />
                </TabPanel>
                <TabPanel>
                    <TemplateDetailsEditor
                        history={props.history}
                        onChange={handleTemplateUpdate.bind(this)}
                        isSaving={isSaving}
                        onSaveClick={() => {
                            setSaveChangedFields(diffTemplateFields(savedSnapshot, props.values.templateConfiguration));
                            setShowSaveModal(true);
                        }}
                        templateConfigurationContainer={props.templateConfigContainer}
                        templateConfiguration={props.values.templateConfiguration}
                        mediaContainer={props.mediaContainer}
                        organizationId={props.orgContainer.state.currentOrganization?.id} />
                </TabPanel>
            </FullPageTabbedPanel>
            <TemplateSaveChangesModal
                showSaveChangesModal={showSaveModal}
                closeModal={() => setShowSaveModal(false)}
                changedFields={saveChangedFields}
                hideExplanation
                submitChanges={props.submitForm}
                isLoading={isSaving}
                hasErrors={false}
            />
            <PageError errors={props.templateConfigContainer.state.errors} onClear={() => props.templateConfigContainer.clearErrors()} />
        </form>
    );

}

const TemplateConfigurationForm = withFormik<TemplateConfigurationContentProps, TemplateConfigurationFormData>({
    mapPropsToValues: (props) => ({
        templateConfiguration: props.templateConfiguration
    }),
    validationSchema: yup.object().shape({
        templateConfiguration: yup.object().shape({

            // NOTE: if we want to do client-side validation on fields, we can use this - but it doesn't fit 
            // well since we have the ability to navigate between pages and it will hide field errors

            // name: yup.string().required("You must provide a name for your template"),
            // description: yup.string().required("You must provide a description for your template"),
            // templateForm: yup.object().shape({
            //     templateFormSections: yup.array().of(
            //         yup.object().shape({
            //             title: yup.string().required("You must enter a title for this section"),
            //             templateFormFields: yup.array().of(
            //                 yup.object().shape({
            //                     title: yup.string().required("You must enter a title for this field")
            //                 })
            //             )
            //         })
            //     ).required("You need at least one section")
            // }).required("You need a form for a template")
        }).required("You need to send a valid configuration")
    }),
    handleSubmit: async (values, { props }) => {
        if (!values.templateConfiguration.currentVersion) {
            values.templateConfiguration.currentVersion = {
                versionName: '1.0'
            }
        }
        await props.templateConfigContainer.updateTemplateConfiguration(values.templateConfiguration);
    }
})(TemplateConfigurationContent);

export default TemplateConfigurationForm;